import Link from 'next/link'
import React, { useEffect, useState } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { faCircleChevronRight } from '@fortawesome/pro-light-svg-icons/faCircleChevronRight'
import { Grid } from '@mui/material'

import { Icon } from '../Icon/Icon'
import { useMegaMenuContext } from './MegaMenuContext'
import { navMenuDataNodeProps } from './mocks'
import { StyledHeaderLinkTypography, StyledLinkButton, StyledLinkTypography } from './styledMenuLinks'
import { StyledCategoryGroup, StyledCategoryTabPanelContainer, StyledTabPanel } from './styledTabPanel'

export type MegaMenuTabPanelProps = {
  children?: React.ReactNode
  idx: number
}

export const MegaMenuTabPanel: React.FC<MegaMenuTabPanelProps> = ({ idx, children }) => {
  const { menuState } = useMegaMenuContext()

  return (
    <StyledTabPanel $visible={menuState.megaMenuSelect === idx}>
      {children}
    </StyledTabPanel>
  )
}

export type CategoryTabPanelProps = {
  subMenu: navMenuDataNodeProps[]
  name: string
  href?: string
}

export const CategoryTabPanel: React.FC<CategoryTabPanelProps> = ({ subMenu, name, href }) => {
  const [split, setSplit] = useState([subMenu.length, subMenu.length, subMenu.length])

  const allCategoryLink = href && (
    <Link href={href}>
      <StyledLinkButton
        disableRipple
        sx={{ marginTop: pxToRem(18), marginLeft: pxToRem(20) }}
        endIcon={
          <Icon
            icon={faCircleChevronRight}
            sx={{ width: '18px' }}
            width={18}
            height={18}
          />
        }
      >
        All {name}
      </StyledLinkButton>
    </Link>
  )

  useEffect(() => {
    let i = 0
    let totalLength = 0
    const splitLengths = [0, 0, 0]
    let splitInd = 0
    const splitTemp = [subMenu.length, subMenu.length, subMenu.length]

    for (i = 0; i < subMenu.length; i += 1) {
      totalLength += 1

      const node = subMenu[i].subMenu

      if (node !== undefined) {
        totalLength += node.length
      }

      if (totalLength > 12) {
        splitTemp[splitInd] = i

        // new split
        splitInd += 1

        // Get each split length
        const oldLength = splitInd > 0 ? splitLengths[splitInd - 1] : 0

        splitLengths[splitInd] = totalLength - oldLength
      }
    }

    setSplit(splitTemp)
  }, [subMenu])


  return (
    <StyledCategoryTabPanelContainer container>
      <Grid item xs={4} sm={4} md={4}>
        <Grid container direction="column">
          {subMenu.slice(0, split[0]).map((node, idx) => (
            <StyledCategoryGroup key={idx}>
              <Grid item key={idx}>
                <Link href={node.href}>
                  <StyledHeaderLinkTypography
                    variant="body1"
                  >
                    {node.name}
                  </StyledHeaderLinkTypography>
                </Link>
              </Grid>
              {node.subMenu &&
                node.subMenu.map((node2, idx2) => (
                  <Grid item key={idx2}>
                    <Link href={node.href}>
                      <StyledLinkTypography variant="body1">
                        {node2.name}
                      </StyledLinkTypography>
                    </Link>
                  </Grid>
                ))}
            </StyledCategoryGroup>
          ))}
        </Grid>
        {(split[1] - split[0] === 0) && allCategoryLink}
      </Grid>

      <Grid item xs={4} sm={4} md={4}>
        {subMenu.slice(split[0], split[1]).map((node, idx) => (
          <StyledCategoryGroup key={idx}>
            <Link href={node.href}>
              <StyledHeaderLinkTypography variant="body1">
                {node.name}
              </StyledHeaderLinkTypography>
            </Link>
            {node.subMenu &&
              node.subMenu.map((node2, idx2) => (
                <Link href={node.href} key={idx2}>
                  <StyledLinkTypography variant="body1">
                    {node2.name}
                  </StyledLinkTypography>
                </Link>
              ))}
          </StyledCategoryGroup>
        ))}
        {(split[1] - split[0] !== 0 && split[1] - subMenu.length === 0) && allCategoryLink}
      </Grid>

      <Grid item xs={4} sm={4} md={4}>
        {subMenu.slice(split[1], subMenu.length).map((node, idx) => (
          <StyledCategoryGroup key={idx}>
            <Link href={node.href}>
              <StyledHeaderLinkTypography variant="body1">
                {node.name}
              </StyledHeaderLinkTypography>
            </Link>
            {node.subMenu &&
              node.subMenu.map((node2, idx2) => (
                <Link href={node2.href} key={idx2}>
                  <StyledLinkTypography variant="body1">
                    {node2.name}
                  </StyledLinkTypography>
                </Link>
              ))}
          </StyledCategoryGroup>
        ))}

        {(split[1] - subMenu.length > 0) && allCategoryLink}
      </Grid>
    </StyledCategoryTabPanelContainer>
  )
}
